<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps({
  modelValue: {
    type: Number,
    required: true,
    default: 1
  },
  total: {
    type: Number,
    required: true,
    default: 1
  },
  mode: {
    type: String,
    default: 'numeric' // 'numeric' | 'temporal'
  },
  temporalList: {
    type: Array as () => string[],
    default: (): string[] => []
  },
  seoMode: {
    type: String,
    default: 'default' // 'default' | 'seo'
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

// Rule 1: Boundary Protection (1 <= current <= total)
const currentPage = computed({
  get: () => Math.max(1, Math.min(props.total, props.modelValue)),
  set: (val) => {
    let newVal = Math.max(1, Math.min(props.total, val))
    if (newVal !== props.modelValue) {
      emit('update:modelValue', newVal)
      emit('change', newVal)
    }
  }
})

const goPrev = () => {
  if (currentPage.value > 1) {
    currentPage.value--
  }
}

const goNext = () => {
  if (currentPage.value < props.total) {
    currentPage.value++
  }
}

// 通用的页码生成器
const generatePages = (current: number, total: number, middleCount: number) => {
  const radius = Math.floor(middleCount / 2)
  const maxShown = middleCount + 4

  // 如果总页数较少，直接全部显示
  if (total <= maxShown) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  const showLeftEllipsis = current > radius + 3
  const showRightEllipsis = current < total - (radius + 2)

  const pages: (number | string)[] = [1]

  // 处理左侧
  if (showLeftEllipsis) {
    pages.push('...')
  } else {
    for (let i = 2; i < current - radius; i++) {
      if (!pages.includes(i)) pages.push(i)
    }
  }

  // 处理中间连续部分
  let adjustedStart = Math.max(2, current - radius)
  let adjustedEnd = Math.min(total - 1, current + radius)

  if (!showLeftEllipsis) {
    adjustedEnd = Math.min(total - 1, middleCount + 2)
  }
  if (!showRightEllipsis) {
    adjustedStart = Math.max(2, total - (middleCount + 1))
  }

  for (let i = adjustedStart; i <= adjustedEnd; i++) {
    if (!pages.includes(i)) pages.push(i)
  }

  // 处理右侧
  if (showRightEllipsis) {
    pages.push('...')
  } else {
    for (let i = adjustedEnd + 1; i < total; i++) {
      if (!pages.includes(i)) pages.push(i)
    }
  }

  if (!pages.includes(total)) {
    pages.push(total)
  }

  return pages
}

// 移动端：中间显示3页
const mobilePageNumbers = computed(() => generatePages(currentPage.value, props.total, 3))
// PC端：中间显示5页
const pcPageNumbers = computed(() => generatePages(currentPage.value, props.total, 5))

// 时间模式（移动端）：始终展示3个，选中的居中
const mobileTemporalPages = computed(() => {
  const current = currentPage.value
  return [current - 1, current, current + 1]
})

// 时间模式（PC端）：始终展示5个，选中的居中
const pcTemporalPages = computed(() => {
  const current = currentPage.value
  return [current - 2, current - 1, current, current + 1, current + 2]
})
</script>

<template>
  <div class="w-full select-none" v-if="mode === 'numeric'">
    <!-- 移动端 UI (vw < 768px): 居中对齐，中间3页 -->
    <div
      v-if="seoMode === 'default'"
      class="flex md:hidden items-center justify-center gap-[4px] sm:gap-1.5 w-full px-4 sm:px-6">
      <button
        @click="goPrev"
        :disabled="currentPage === 1"
        class="flex-1 aspect-square max-w-[40px] flex items-center justify-center rounded bg-[#F6F6F6] text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed active:bg-gray-200 transition-colors">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path
            fill-rule="evenodd"
            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
            clip-rule="evenodd" />
        </svg>
      </button>

      <template v-for="(item, index) in mobilePageNumbers" :key="index">
        <!-- 省略号：无背景，不可点击 -->
        <div
          v-if="item === '...'"
          class="flex-1 aspect-square max-w-[40px] flex items-center justify-center text-gray-400 cursor-default tracking-widest text-[14px]">
          ...
        </div>
        <!-- 页码：未选中浅灰，选中深灰且下划线 -->
        <button
          v-else
          @click="currentPage = item as number"
          :class="[
            'flex-1 aspect-square max-w-[40px] px-1 rounded flex items-center justify-center text-[14px] sm:text-[15px] font-medium relative transition-colors',
            currentPage === item
              ? 'bg-[#E3E3E3]  text-gray-800 font-bold after:content-[\'\'] after:absolute after:bottom-[4px] after:left-1/2 after:-translate-x-1/2 after:w-[14px] after:h-[2px] after:bg-gray-800'
              : 'bg-[#F6F6F6] text-gray-600 active:bg-gray-200'
          ]">
          {{ item }}
        </button>
      </template>

      <button
        @click="goNext"
        :disabled="currentPage === total"
        class="flex-1 aspect-square max-w-[40px] flex items-center justify-center rounded bg-[#F6F6F6] text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed active:bg-gray-200 transition-colors">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path
            fill-rule="evenodd"
            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
            clip-rule="evenodd" />
        </svg>
      </button>
    </div>

    <!-- SEO 移动端 UI: 一行9个 -->
    <div v-else class="grid md:hidden grid-cols-9 gap-[4px] sm:gap-1.5 w-full px-4 sm:px-6">
      <NuxtLink
        v-for="item in total"
        :key="item"
        :to="{ query: { ...$route.query, page: item } }"
        @click.prevent="currentPage = item"
        :class="[
          'aspect-square w-full rounded flex items-center justify-center text-[13px] sm:text-[14px] font-medium relative transition-colors',
          currentPage === item
            ? 'bg-[#E3E3E3]  text-gray-800 font-bold after:content-[\'\'] after:absolute after:bottom-[4px] after:left-1/2 after:-translate-x-1/2 after:w-[14px] after:h-[2px] after:bg-gray-800'
            : 'bg-[#F6F6F6] text-gray-600 active:bg-gray-200'
        ]">
        {{ item }}
      </NuxtLink>
    </div>

    <!-- PC端 UI (vw >= 768px): 居中对齐，中间5页 -->
    <div v-if="seoMode === 'default'" class="hidden md:flex items-center justify-center gap-1.5 w-full">
      <button
        @click="goPrev"
        :disabled="currentPage === 1"
        class="h-8 min-w-[32px] flex items-center justify-center rounded bg-[#F6F6F6] text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#EBEBEB] transition-colors">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path
            fill-rule="evenodd"
            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
            clip-rule="evenodd" />
        </svg>
      </button>

      <template v-for="(item, index) in pcPageNumbers" :key="index">
        <!-- 省略号：无背景，不可点击 -->
        <div
          v-if="item === '...'"
          class="h-8 min-w-[24px] flex items-center justify-center text-gray-400 cursor-default tracking-widest">
          ...
        </div>
        <!-- 页码：未选中浅灰，选中深灰且下划线 -->
        <button
          v-else
          @click="currentPage = item as number"
          :class="[
            'h-8 min-w-[32px] px-1 rounded flex items-center justify-center text-[14px] font-medium relative transition-colors',
            currentPage === item
              ? 'bg-[#E3E3E3]  text-gray-800 font-bold after:content-[\'\'] after:absolute after:bottom-[3px] after:left-1/2 after:-translate-x-1/2 after:w-[14px] after:h-[2px] after:bg-gray-800'
              : 'bg-[#F6F6F6] text-gray-600 hover:bg-[#EBEBEB]'
          ]">
          {{ item }}
        </button>
      </template>

      <button
        @click="goNext"
        :disabled="currentPage === total"
        class="h-8 min-w-[32px] flex items-center justify-center rounded bg-[#F6F6F6] text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#EBEBEB] transition-colors">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path
            fill-rule="evenodd"
            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
            clip-rule="evenodd" />
        </svg>
      </button>
    </div>

    <!-- SEO PC端 UI: 自动换行并左对齐，适当增加显示数量 -->
    <div v-else class="hidden md:flex flex-wrap items-center justify-start gap-1.5 w-full px-4">
      <NuxtLink
        v-for="item in total"
        :key="item"
        :to="{ query: { ...$route.query, page: item } }"
        @click.prevent="currentPage = item"
        :class="[
          'h-8 min-w-[32px] px-1 rounded flex items-center justify-center text-[14px] font-medium relative transition-colors',
          currentPage === item
            ? 'bg-[#E3E3E3]  text-gray-800 font-bold after:content-[\'\'] after:absolute after:bottom-[3px] after:left-1/2 after:-translate-x-1/2 after:w-[14px] after:h-[2px] after:bg-gray-800'
            : 'bg-[#F6F6F6] text-gray-600 hover:bg-[#EBEBEB]'
        ]">
        {{ item }}
      </NuxtLink>
    </div>
  </div>

  <!-- 时间模式 -->
  <div v-else-if="mode === 'temporal'" class="w-full select-none">
    <!-- 移动端 UI (vw < 768px): 居中对齐，中间3页，有左右箭头 -->
    <div v-if="seoMode === 'default'" class="flex md:hidden items-center justify-center gap-2 w-full px-4 sm:px-6">
      <button
        @click="goPrev"
        :disabled="currentPage === 1"
        class="h-8 min-w-[32px] flex items-center justify-center rounded bg-[#F6F6F6] text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed active:bg-gray-200 transition-colors">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path
            fill-rule="evenodd"
            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
            clip-rule="evenodd" />
        </svg>
      </button>

      <div class="flex flex-1 items-center gap-2 justify-center overflow-hidden">
        <template v-for="(item, index) in mobileTemporalPages" :key="index">
          <div v-if="item < 1 || item > total" class="flex-1 max-w-[100px] h-8 invisible"></div>
          <button
            v-else
            @click="currentPage = item"
            :class="[
              'flex-1 max-w-[100px] h-8 px-1 sm:px-2 rounded flex items-center justify-center text-[13px] sm:text-[14px] font-medium relative transition-colors whitespace-nowrap overflow-hidden text-ellipsis',
              currentPage === item
                ? 'bg-[#E3E3E3]  text-gray-800 font-bold after:content-[\'\'] after:absolute after:bottom-[3px] after:left-1/2 after:-translate-x-1/2 after:w-[14px] after:h-[2px] after:bg-gray-800'
                : 'bg-[#F6F6F6] text-gray-600 active:bg-gray-200'
            ]">
            {{ temporalList[item - 1] }}
          </button>
        </template>
      </div>

      <button
        @click="goNext"
        :disabled="currentPage === total"
        class="h-8 min-w-[32px] flex items-center justify-center rounded bg-[#F6F6F6] text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed active:bg-gray-200 transition-colors">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path
            fill-rule="evenodd"
            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
            clip-rule="evenodd" />
        </svg>
      </button>
    </div>

    <!-- SEO 移动端 UI: 一行3个 -->
    <div v-else class="grid md:hidden grid-cols-3 gap-2 w-full px-4 sm:px-6">
      <NuxtLink
        v-for="item in total"
        :key="item"
        :to="{ query: { ...$route.query, page: item } }"
        @click.prevent="currentPage = item"
        :class="[
          'h-8 px-1 sm:px-2 rounded flex items-center justify-center text-[13px] sm:text-[14px] font-medium relative transition-colors whitespace-nowrap overflow-hidden text-ellipsis',
          currentPage === item
            ? 'bg-[#E3E3E3]  text-gray-800 font-bold after:content-[\'\'] after:absolute after:bottom-[3px] after:left-1/2 after:-translate-x-1/2 after:w-[14px] after:h-[2px] after:bg-gray-800'
            : 'bg-[#F6F6F6] text-gray-600 active:bg-gray-200'
        ]">
        {{ temporalList[item - 1] }}
      </NuxtLink>
    </div>

    <!-- PC端 UI (vw >= 768px): 居中对齐，中间5页，有左右箭头 -->
    <div v-if="seoMode === 'default'" class="hidden md:flex items-center justify-center gap-2 w-full">
      <button
        @click="goPrev"
        :disabled="currentPage === 1"
        class="h-8 min-w-[32px] flex items-center justify-center rounded bg-[#F6F6F6] text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#EBEBEB] transition-colors">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path
            fill-rule="evenodd"
            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
            clip-rule="evenodd" />
        </svg>
      </button>

      <div class="flex items-center gap-2 justify-center">
        <template v-for="(item, index) in pcTemporalPages" :key="index">
          <div v-if="item < 1 || item > total" class="w-[100px] h-8 invisible"></div>
          <button
            v-else
            @click="currentPage = item"
            :class="[
              'w-[100px] h-8 px-2 rounded flex items-center justify-center text-[14px] font-medium relative transition-colors whitespace-nowrap',
              currentPage === item
                ? 'bg-[#E3E3E3]  text-gray-800 font-bold after:content-[\'\'] after:absolute after:bottom-[3px] after:left-1/2 after:-translate-x-1/2 after:w-[14px] after:h-[2px] after:bg-gray-800'
                : 'bg-[#F6F6F6] text-gray-600 hover:bg-[#EBEBEB]'
            ]">
            {{ temporalList[item - 1] }}
          </button>
        </template>
      </div>

      <button
        @click="goNext"
        :disabled="currentPage === total"
        class="h-8 min-w-[32px] flex items-center justify-center rounded bg-[#F6F6F6] text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#EBEBEB] transition-colors">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path
            fill-rule="evenodd"
            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
            clip-rule="evenodd" />
        </svg>
      </button>
    </div>

    <!-- SEO PC端 UI: 自动换行并左对齐，适当增加显示数量 -->
    <div v-else class="hidden md:flex flex-wrap items-center justify-start gap-2 w-full px-4">
      <NuxtLink
        v-for="item in total"
        :key="item"
        :to="{ query: { ...$route.query, page: item } }"
        @click.prevent="currentPage = item"
        :class="[
          'w-[100px] h-8 px-2 rounded flex items-center justify-center text-[14px] font-medium relative transition-colors whitespace-nowrap',
          currentPage === item
            ? 'bg-[#E3E3E3]  text-gray-800 font-bold after:content-[\'\'] after:absolute after:bottom-[3px] after:left-1/2 after:-translate-x-1/2 after:w-[14px] after:h-[2px] after:bg-gray-800'
            : 'bg-[#F6F6F6] text-gray-600 hover:bg-[#EBEBEB]'
        ]">
        {{ temporalList[item - 1] }}
      </NuxtLink>
    </div>
  </div>
</template>
